"use client";
import { API_URL } from "@/lib/config";

// Rewritten: 852-line god component → ~130-line orchestration root.
// All logic lives in 3 hooks; large JSX blocks are focused components.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import { AlertCircle, Shuffle, Square } from "lucide-react";
import { useAuth, AuthModal } from "@/features/auth";
import { MapViewer, SpiderFeature, ScreenOrigin, FilterPanel, DEFAULT_FILTERS, TimeTravelSlider } from "@/features/map";
import { Sidebar, ExploreTabId, GlobalSearchPalette, AnnouncementBanner } from "@/shared/ui";
import { client } from "@/api-client/client.gen";
import { usePanoramaData }    from "./hooks/usePanoramaData";
import { useActivePanorama }  from "./hooks/useActivePanorama";
import { useSlideshow, SLIDESHOW_INTERVALS, SlideshowMs } from "./hooks/useSlideshow";
import { useGlow } from "./hooks/useGlow";
import TopNavBar    from "./components/TopNavBar";
import TopGlassMenu from "./components/TopGlassMenu";
import { MapStyleKey } from "@/features/map/mapTheme";
import { WelcomeModal, OnboardingTour, useHelp } from "@/features/help";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAndroidBack, type CloseAction } from "@/hooks/useAndroidBack";
import { useRouteBuilder } from "@/features/routebuilder/useRouteBuilder";
import RouteBuilderPanel from "@/features/routebuilder/RouteBuilderPanel";
import { useTourRoute } from "@/features/tourroute/useTourRoute";
import TourRoutePlayer from "@/features/tourroute/TourRoutePlayer";
import type { Waypoint } from "@/features/routebuilder/types";
import { useGpxRoute } from "@/features/gpxroute/useGpxRoute";
import GpxRoutePanel from "@/features/gpxroute/GpxRoutePanel";
import type { DashTab } from "@/features/library/types";
import { useTheme } from "@/features/theme";

// Configure generated API client to point to our local FastAPI service
client.setConfig({ baseURL: API_URL, withCredentials: true });

const PersonalDashboard = dynamic(() => import("@/features/library/PersonalDashboard"), { ssr: false });

const PanoramaViewer = dynamic(() => import("@/features/viewer/PanoramaViewer"), {
  ssr: false,
  // Hidden — awaiting deletion after confirmation
  // loading: () => (
  //   <div className="absolute inset-0 z-50 bg-black flex items-center justify-center">
  //     <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
  //   </div>
  // ),
});

export default function Home() {
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const {
    tourScope, tourActive, tourSteps, tourStepIndex,
    endTour, helpCenterOpen, closeHelpCenter, hasSeenWelcome, dismissWelcome,
  } = useHelp();
  const isMobile = useIsMobile();
  const tourOpenedSidebar = useRef(false);

  // ── Simple UI state (no logic, no effects) ────────────────────────────────
  const [isSidebarOpen,     setIsSidebarOpen]     = useState(false);
  const [exploreTabId,      setExploreTabId]      = useState<ExploreTabId>("trending");
  const [isUploading,       setIsUploading]       = useState(false);
  const [dashboardDefaultTab, setDashboardDefaultTab] = useState<DashTab | undefined>(undefined);
  const [dashboardDefaultCollectionId, setDashboardDefaultCollectionId] = useState<string | undefined>(undefined);
  const [showAuthModal,     setShowAuthModal]     = useState(false);
  const [isMapInteracting,  setIsMapInteracting]  = useState(false);
  const [filterPanelOpen,   setFilterPanelOpen]   = useState(false);
  const { isDark, resolved: resolvedTheme } = useTheme();
  const [mapStyle, setMapStyle] = useState<MapStyleKey>("dark");
  // A map style the user picked wins; otherwise the map follows the app theme.
  useEffect(() => {
    const stored = localStorage.getItem("360map-style") as MapStyleKey | null;
    setMapStyle(stored ?? resolvedTheme);
  }, [resolvedTheme]);

  // ── Photo transition state ────────────────────────────────────────────────
  const [transitionFeature, setTransitionFeature] = useState<{
    id: string;
    thumbnailUrl?: string;
    origin: ScreenOrigin;
  } | null>(null);
  // True when the viewer was opened early (at 10% zoom-in) — drives 3s fade-in
  const [isEarlyOpen, setIsEarlyOpen] = useState(false);

  const handleNavigationStart = useCallback((feature: SpiderFeature, origin: ScreenOrigin) => {
    setTransitionFeature({ id: feature.id, thumbnailUrl: feature.thumbnailUrl, origin });
  }, []);
  // ── Glow state ────────────────────────────────────────────────────────────
  const glassMenuGlow = useGlow(6000);   // 360Photos bar — 6 s pulse + 1 s fade = 7 s total
  const navGlow       = useGlow(4000);   // Nav toggle button — 4 s pulse + 1 s fade = 5 s total

  // Track open/expanded states of both menus to extend glow when both are open
  const [isNavMenuOpen,        setIsNavMenuOpen]        = useState(false);
  const [isGlassMenuExpanded,  setIsGlassMenuExpanded]  = useState(false);

  const handleGlassMenuExpandedChange = useCallback((expanded: boolean) => setIsGlassMenuExpanded(expanded), []);

  // Extend both glows by 2 s when the user has both menus open simultaneously
  useEffect(() => {
    if (isNavMenuOpen && isGlassMenuExpanded) {
      glassMenuGlow.extendGlow(2000);
      navGlow.extendGlow(2000);
    }
  // extendGlow is stable (useCallback), but we only want to fire on the combo change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNavMenuOpen, isGlassMenuExpanded]);

  useEffect(() => { setIsSidebarOpen(window.innerWidth >= 768); }, []);

  // Close filter panel when sidebar closes
  useEffect(() => { if (!isSidebarOpen) setFilterPanelOpen(false); }, [isSidebarOpen]);

  // On mobile, auto-open the sidebar when the tour reaches the "sidebar" step, then close it when leaving
  useEffect(() => {
    if (!tourActive || !isMobile) return;
    const step = tourSteps[tourStepIndex];
    if (step?.id === "sidebar" || step?.id === "filter-button") {
      tourOpenedSidebar.current = true;
      setIsSidebarOpen(true);
    } else if (tourOpenedSidebar.current) {
      tourOpenedSidebar.current = false;
      setIsSidebarOpen(false);
    }
  }, [tourActive, tourSteps, tourStepIndex, isMobile]);

  // ── Hooks ─────────────────────────────────────────────────────────────────
  const panoData      = usePanoramaData(user?.id, user?.username);
  const active        = useActivePanorama(panoData.data);
  const slideshow     = useSlideshow(panoData.filteredData, exploreTabId);
  const routeBuilder  = useRouteBuilder(panoData.data ?? null);
  const gpxRoute      = useGpxRoute();

  // Proxy ref: always returns the live raw MapLibre map from the handle
  const rawMapForTour = useMemo(() => ({
    get current() {
      return (active.mapViewerRef.current as any)?.getRawMap?.() ?? null;
    },
  }), [active.mapViewerRef]);

  const onArriveAtWaypoint = useCallback((wp: Waypoint) => {
    const feature = panoData.data?.features.find((f: any) => f.properties?.id === wp.panoId);
    if (!feature) return;
    const p = feature.properties as any;
    active.handleSelectPanorama(
      {
        id: p.id,
        baseTileUrl: p.base_tile_url ?? "",
        previewUrl: p.preview_url ?? undefined,
        thumbnailUrl: p.thumbnail_url ?? undefined,
        name: p.name ?? undefined,
      },
      {
        x: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
        y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
      },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panoData.data]);

  const onDismissPanorama = useCallback(() => {
    active.setActivePanorama(null);
    active.setTourHistory([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tourRoute = useTourRoute({
    waypoints:        routeBuilder.waypoints,
    routeGeometry:    routeBuilder.routeGeometry,
    routeDurationSec: routeBuilder.routeInfo?.durationSec ?? null,
    travelMode:       routeBuilder.travelMode,
    mapHandleRef:     active.mapViewerRef,
    onArriveAtWaypoint,
    onDismissPanorama,
  });

  // ── Viewer back handler — behaviour depends on how the viewer was opened ──
  // One step back, in this order: library → reopen the library; portal history →
  // previous photo; external (came from Explore / a profile / a shared link) →
  // really navigate back to that page; otherwise (opened from a map marker) →
  // close the viewer, back to the map. The map case used to do nothing yet still
  // report "handled", so Back was silently swallowed while a photo was open.
  const handleViewerBack = useCallback((): boolean | "leave" => {
    if (active.viewerSource === "library") {
      active.setActivePanorama(null);
      active.setTourHistory([]);
      setDashboardDefaultTab("library");
      setIsUploading(true);
      return true;
    }
    if (active.viewerSource === "tourManager") {
      active.setActivePanorama(null);
      active.setTourHistory([]);
      setDashboardDefaultTab("collections");
      setDashboardDefaultCollectionId(active.tourManagerCollectionId ?? undefined);
      setIsUploading(true);
      return true;
    }
    if (active.tourHistory.length > 0) {
      active.handleBack();
      return true;
    }
    if (active.viewerSource === "external") {
      window.history.back();
      return "leave";
    }
    active.setActivePanorama(null);
    active.setTourHistory([]);
    tourRoute.notifyViewingEnd();
    return true;
  }, [active, tourRoute]);

  const viewerOnBack =
    active.viewerSource === "library" ||
    active.viewerSource === "tourManager" ||
    active.viewerSource === "external" ||
    active.tourHistory.length > 0
      ? handleViewerBack
      : undefined;

  // ── Back-button handler (desktop + mobile) ────────────────────────────────
  // Priority: viewer → dashboard → auth → welcome → help center → tour →
  //           slideshow menu → filter panel → nav menu → sidebar → exit dialog
  const backActions: CloseAction[] = [
    () => (active.activePanorama ? handleViewerBack() : false),
    () => { if (isUploading)    { setIsUploading(false);       return true; } return false; },
    () => { if (showAuthModal)  { setShowAuthModal(false);     return true; } return false; },
    () => { if (!hasSeenWelcome){ dismissWelcome();             return true; } return false; },
    () => { if (helpCenterOpen) { closeHelpCenter();            return true; } return false; },
    () => { if (tourActive)     { endTour();                    return true; } return false; },
    () => { if (slideshow.slideshowMenuOpen) { slideshow.setSlideshowMenuOpen(false); return true; } return false; },
    () => { if (filterPanelOpen){ setFilterPanelOpen(false);   return true; } return false; },
    () => { if (isNavMenuOpen)  { setIsNavMenuOpen(false);     return true; } return false; },
    () => { if (isSidebarOpen)  { setIsSidebarOpen(false);     return true; } return false; },
  ];
  const { exitDialogOpen, confirmExit, cancelExit } = useAndroidBack(backActions);

  // Called after 800 ms into a long flyTo — opens viewer with slow 3 s fade-in
  const handleNavigationProgress = (feature: SpiderFeature) => {
    setIsEarlyOpen(true);
    active.handleSelectPanorama(feature, {
      x: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
      y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
    });
  };

  // Clear transition state when viewer closes
  useEffect(() => {
    if (!active.activePanorama) {
      setTransitionFeature(null);
      setIsEarlyOpen(false);
    }
  }, [active.activePanorama]);

  // Close the route builder panel when a tour starts so the map is visible.
  // The TourRoutePlayer's Dismiss button calls routeBuilder.toggle() to reopen it.
  useEffect(() => {
    if (tourRoute.phase !== "idle" && tourRoute.phase !== "complete") {
      routeBuilder.close();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRoute.phase]);

  // Center the map on the most-recently uploaded panorama (any user)
  const initialMapCenter = useMemo(() => {
    const features = panoData.data?.features ?? [];
    if (!features.length) return undefined;
    const latest = features.reduce((best, f) => {
      const fd = (f.properties as any).created_at ?? "";
      const bd = (best.properties as any).created_at ?? "";
      return fd > bd ? f : best;
    });
    const [longitude, latitude] = latest.geometry.coordinates as [number, number];
    return { longitude, latitude };
  }, [panoData.data]);

  const filterDirty =
    panoData.filters.minRating > 0 ||
    panoData.filters.dateFrom !== "" ||
    panoData.filters.dateTo   !== "" ||
    panoData.filters.hideHotspotTypes.length > 0;

  // Wrap slideshow select: set interval + close menu
  const handleSlideshowSelect = (ms: SlideshowMs | null) => {
    slideshow.setSlideshowInterval(ms);
    slideshow.setSlideshowMenuOpen(false);
  };

  // Navigate Here — GPS coords for the active panorama.
  // GeoJSON stores [longitude, latitude]; prop wants [latitude, longitude].
  const activePanoCoords: [number, number] | undefined = (() => {
    if (!active.activePanorama) return undefined;
    const feat = panoData.data?.features.find(
      (f: any) => f.properties?.id === active.activePanorama!.id,
    );
    const [lng, lat] = (feat?.geometry?.coordinates ?? [0, 0]) as [number, number];
    return lat !== 0 || lng !== 0 ? [lat, lng] : undefined;
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  // h-dvh (not h-screen/100vh) — iPad/mobile Safari's dynamic toolbar
  // shrinks the real viewport after load, so a 100vh container ends up
  // taller than what's actually visible, clipping the absolutely
  // positioned header and account dropdown underneath the URL bar.
  return (
    <main className="relative w-full h-dvh bg-canvas text-ink overflow-hidden">
      <AnnouncementBanner />
      <WelcomeModal />
      <OnboardingTour
        paused={!!active.activePanorama && tourScope !== "viewer"}
        noBlocker={tourScope === "viewer"}
      />
      {/* Phase 2: Global Cmd+K search palette */}
      <GlobalSearchPalette
        onOpenPanorama={(id) => {
          const feature = panoData.data?.features?.find((f: any) => f.properties?.id === id);
          if (feature) {
            const p = feature.properties as any;
            active.handleSelectPanorama(
              {
                id,
                baseTileUrl: p.base_tile_url ?? "",
                previewUrl: p.preview_url ?? undefined,
                thumbnailUrl: p.thumbnail_url ?? undefined,
                name: p.name ?? undefined,
              },
              { x: typeof window !== "undefined" ? window.innerWidth / 2 : 400,
                y: typeof window !== "undefined" ? window.innerHeight / 2 : 300 }
            );
          }
        }}
      />

      {/* Empty database fallback */}
      {!panoData.isLoading && panoData.data && panoData.data.features.length === 0 && (
        <div className="absolute inset-0 z-40 bg-black/40 flex items-center justify-center pointer-events-none">
          <div className="bg-tone-900 border border-tone-800 p-8 rounded-2xl flex flex-col items-center justify-center space-y-4 max-w-sm text-center shadow-2xl">
            <AlertCircle className="w-12 h-12 text-yellow-500" />
            <h2 className="text-xl font-bold">No Panoramas Available</h2>
            <p className="text-sm text-tone-400">
              The Postgres database is currently empty. Upload images via the backend pipeline to populate the map!
            </p>
          </div>
        </div>
      )}

      {/* MapLibre canvas — always mounted; hidden behind viewer */}
      {panoData.filteredData && (
        <div data-tour-id="map-canvas" className={`transition-opacity ${active.activePanorama ? "opacity-0" : "opacity-100"} ${isEarlyOpen ? "duration-1000" : "duration-500"}`}>
          <MapViewer
            ref={active.mapViewerRef}
            geoJsonData={panoData.filteredData}
            onSelectPanorama={active.handleSelectPanorama}
            onMapInteractionChange={setIsMapInteracting}
            onNavigationStart={handleNavigationStart}
            onNavigationProgress={handleNavigationProgress}
            pendingFeatureId={transitionFeature?.id}
            initialCenter={initialMapCenter}
            mapStyle={mapStyle}
            onMapStyleChange={setMapStyle}
            routeBuilderMode={routeBuilder.isAddingMode || gpxRoute.isAddingMode}
            onRouteWaypointAdd={gpxRoute.isAddingMode ? gpxRoute.addWaypoint : routeBuilder.addWaypoint}
            routeWaypointIds={new Set(routeBuilder.waypoints.map((w) => w.panoId))}
            routeWaypoints={routeBuilder.waypoints}
            routeGeometry={routeBuilder.routeGeometry}
            gpxRouteGeometry={gpxRoute.trimmedGeoJson}
            gpxRouteColor={gpxRoute.settings.routeColor}
            gpxRouteDash={gpxRoute.settings.routeDash}
            gpxRouteWidth={gpxRoute.settings.routeWidth}
            suppressInteractionFade={tourRoute.phase !== "idle" && tourRoute.phase !== "complete"}
          />
        </div>
      )}

      {/* Deep-link loading screen */}
      <AnimatePresence>
        {(!active.isHashChecked || (active.pendingPanoramaId && !active.activePanorama)) && (
          <motion.div
            key="deep-link-loader"
            data-boot-loader
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
            className="fixed inset-0 z-[400] bg-white dark:bg-[#080c14] flex items-center justify-center"
          >
            {/* Concentric animated rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {[1, 2, 3].map((i) => (
                <motion.span
                  key={i}
                  className="absolute rounded-full border border-blue-500/20"
                  style={{ width: 80 + i * 70, height: 80 + i * 70 }}
                  animate={{ scale: [1, 1.12, 1], opacity: [0.18, 0.06, 0.18] }}
                  transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.55, ease: "easeInOut" }}
                />
              ))}
            </div>

            {/* Center content */}
            <div className="relative flex flex-col items-center gap-5">
              {/* Spinner ring */}
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-full border-2 border-ink/5" />
                <div className="absolute inset-0 rounded-full border-2 border-t-blue-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
                {/* Inner dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-blue-400/60 text-white" />
                </div>
              </div>

              {/* Branding */}
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-200 to-white">
                  360 Maps
                </span>
                <motion.span
                  className="text-xs text-ink/35 font-medium tracking-widest uppercase"
                  animate={{ opacity: [0.35, 0.7, 0.35] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                  Opening panorama…
                </motion.span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/*
       * ── Map UI overlay ─────────────────────────────────────────────────────
       * Single wrapper: fades to 20 % opacity + blocks pointer-events when the
       * map is being panned / zoomed.  Any new buttons or menus added inside
       * this div automatically inherit the behaviour — no extra code needed.
       * Suppressed during deep-link loading so nav/sidebar don't flash.
       */}
      {active.isHashChecked && !active.pendingPanoramaId && <div
        style={{ transition: `opacity ${isMapInteracting ? "180ms" : "350ms"} ease-out` }}
        className={
          tourRoute.phase !== "idle" && tourRoute.phase !== "complete"
            ? "opacity-0 pointer-events-none"
            : isMapInteracting ? "opacity-20 pointer-events-none" : ""
        }
      >
        <TopNavBar
          user={user}
          isAuthLoading={isAuthLoading}
          isGlowing={navGlow.isGlowing}
          menuOpen={isNavMenuOpen}
          onMenuOpenChange={setIsNavMenuOpen}
          onUpload={() => setIsUploading(true)}
          onShowAuth={() => setShowAuthModal(true)}
          onLogout={logout}
          mapStyle={mapStyle}
        />

        <TopGlassMenu
          visible={!active.activePanorama}
          isMapInteracting={isMapInteracting}
          filterPanelOpen={filterPanelOpen}
          mapStyle={mapStyle}
          isGlowing={glassMenuGlow.isGlowing}
          onExpandedChange={handleGlassMenuExpandedChange}
          user={user}
          showMyProfileOnly={panoData.showMyProfileOnly}
          onToggleContributions={() => panoData.setShowMyProfileOnly(!panoData.showMyProfileOnly)}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen((v) => !v)}
          isRouteBuilderOpen={routeBuilder.isOpen}
          onToggleRouteBuilder={routeBuilder.toggle}
          isGpxRouteOpen={gpxRoute.isOpen}
          onToggleGpxRoute={gpxRoute.toggle}
          anyPanelOpen={isSidebarOpen || routeBuilder.isOpen || gpxRoute.isOpen}
          slideshowInterval={slideshow.slideshowInterval}
          slideshowMenuOpen={slideshow.slideshowMenuOpen}
          slideshowMenuRef={slideshow.slideshowMenuRef}
          onSlideshowButtonClick={() => slideshow.setSlideshowMenuOpen((o) => !o)}
          onSlideshowSelect={handleSlideshowSelect}
          slideshowDeckRef={slideshow.slideshowDeckRef}
        />

        {/* ── Sidebar ── */}
        <div
          className={`absolute top-24 left-6 z-[35] pointer-events-none transition-opacity duration-700 ${
            active.activePanorama ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <div className="pointer-events-auto" data-tour-id="sidebar">
            <Sidebar
              panoramas={panoData.filteredData?.features || []}
              isOpen={isSidebarOpen}
              onSetOpen={setIsSidebarOpen}
              exploreTabId={exploreTabId}
              onExploreTabChange={setExploreTabId}
              filterOpen={filterPanelOpen}
              filterDirty={filterDirty}
              filteredCount={panoData.filteredData?.features.length ?? 0}
              onFilterClick={() => setFilterPanelOpen((v) => !v)}
              mapStyle={mapStyle}
              routeBuilderOpen={routeBuilder.isOpen}
              onAddToRoute={routeBuilder.addWaypoint}
              isRouteBuilderOpen={routeBuilder.isOpen}
              onToggleRouteBuilder={routeBuilder.toggle}
            />
          </div>
        </div>

        {/* Filter panel — desktop: beside sidebar, above glass menu (z-[46]) */}
        <AnimatePresence>
          {isSidebarOpen && filterPanelOpen && panoData.data && !active.activePanorama && (
            <div className="absolute top-24 pointer-events-auto hidden md:flex items-start z-[46]" style={{ left: "calc(1.5rem + 320px + 0.75rem)" }}>
              <FilterPanel
                filters={panoData.filters}
                filteredCount={panoData.filteredData?.features.length ?? 0}
                dirty={filterDirty}
                onChange={panoData.setFilters}
                onClose={() => setFilterPanelOpen(false)}
                isDark={isDark}
              />
            </div>
          )}
        </AnimatePresence>

        {/* Filter panel — mobile: fixed overlay at bottom */}
        <AnimatePresence>
          {isSidebarOpen && filterPanelOpen && panoData.data && (
            <motion.div
              key="filter-mobile"
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0,      opacity: 1 }}
              exit={{   y: "100%", opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              className="pointer-events-auto md:hidden fixed bottom-0 inset-x-0 z-[50] p-4"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
            >
              <FilterPanel
                filters={panoData.filters}
                filteredCount={panoData.filteredData?.features.length ?? 0}
                dirty={filterDirty}
                onChange={panoData.setFilters}
                onClose={() => setFilterPanelOpen(false)}
                isDark={isDark}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Route Builder Panel ── */}
        <AnimatePresence>
          {routeBuilder.isOpen && !active.activePanorama && (
            <>
              {isMobile && (
                <motion.div
                  key="route-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[45] bg-black/60 backdrop-blur-sm pointer-events-auto"
                  onClick={routeBuilder.close}
                />
              )}
              <motion.div
                layout
                className={isMobile ? "pointer-events-auto" : "absolute top-24 pointer-events-auto z-[45]"}
                style={isMobile ? undefined : {
                  left: isSidebarOpen
                    ? "calc(1.5rem + 320px + 0.75rem)"
                    : "1.5rem",
                }}
                transition={{ type: "spring", stiffness: 320, damping: 30 }}
              >
                <RouteBuilderPanel
                  waypoints={routeBuilder.waypoints}
                  routeName={routeBuilder.routeName}
                  travelMode={routeBuilder.travelMode}
                  savedRoutes={routeBuilder.savedRoutes}
                  isAddingMode={routeBuilder.isAddingMode}
                  searchQuery={routeBuilder.searchQuery}
                  shareCopied={routeBuilder.shareCopied}
                  savedFlash={routeBuilder.savedFlash}
                  onSetRouteName={routeBuilder.setRouteName}
                  onReorder={routeBuilder.reorderWaypoints}
                  onRemoveWaypoint={routeBuilder.removeWaypoint}
                  onSetTravelMode={routeBuilder.setTravelMode}
                  onSetAddingMode={routeBuilder.setIsAddingMode}
                  onSetSearchQuery={routeBuilder.setSearchQuery}
                  onSave={routeBuilder.saveRoute}
                  onLoadRoute={routeBuilder.loadRoute}
                  onDeleteRoute={routeBuilder.deleteRoute}
                  onOpenInMaps={routeBuilder.openInMaps}
                  onCopyShareLink={routeBuilder.copyShareLink}
                  onExportGPX={routeBuilder.exportGPX}
                  allPanoramas={routeBuilder.allPanoramas}
                  onAddWaypoint={routeBuilder.addWaypoint}
                  onClose={routeBuilder.close}
                  isDark={isDark}
                  isMobile={isMobile}
                  routeInfo={routeBuilder.routeInfo}
                  routeLoading={routeBuilder.routeLoading}
                  onPlayTourRoute={
                    routeBuilder.waypoints.length >= 2 && !routeBuilder.routeLoading
                      ? tourRoute.play
                      : undefined
                  }
                  onCreateTour={
                    routeBuilder.waypoints.length >= 1
                      ? () => routeBuilder.createTour(() => {
                          setDashboardDefaultTab("collections");
                          setIsUploading(true);
                        })
                      : undefined
                  }
                  tourCreating={routeBuilder.tourCreating}
                  tourSettings={tourRoute.settings}
                  onTourSettingsChange={tourRoute.setSettings}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ── GPX Route Panel ── */}
        <AnimatePresence>
          {gpxRoute.isOpen && !active.activePanorama && (
            <>
              {isMobile && (
                <motion.div
                  key="gpx-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[45] bg-black/60 backdrop-blur-sm pointer-events-auto"
                  onClick={gpxRoute.close}
                />
              )}
              <motion.div
                layout
                className={isMobile ? "pointer-events-auto" : "absolute top-24 pointer-events-auto z-[45]"}
                style={isMobile ? undefined : (() => {
                  const slot = (isSidebarOpen ? 1 : 0) + (routeBuilder.isOpen ? 1 : 0);
                  return slot === 0
                    ? { left: "1.5rem" }
                    : { left: `calc(1.5rem + ${slot} * (320px + 0.75rem))` };
                })()}
                transition={{ type: "spring", stiffness: 320, damping: 30 }}
              >
                <GpxRoutePanel
                  {...gpxRoute}
                  isDark={isDark}
                  isMobile={isMobile}
                  onOpenDashboard={() => {
                    setDashboardDefaultTab("collections");
                    setIsUploading(true);
                  }}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

      </div>}{/* end map UI overlay */}

      {/* Tour Route Player — outside the fade wrapper so it stays visible during tours */}
      {active.isHashChecked && !active.pendingPanoramaId && (
        <TourRoutePlayer
          {...tourRoute}
          waypoints={routeBuilder.waypoints}
          rawMapRef={rawMapForTour as any}
          isDark={isDark}
          onDismiss={routeBuilder.toggle}
        />
      )}

      {isUploading && (
        <PersonalDashboard
          onClose={() => { setIsUploading(false); setDashboardDefaultTab(undefined); setDashboardDefaultCollectionId(undefined); }}
          geoJsonData={panoData.data}
          onRefresh={panoData.loadPanoramas}
          onOpenPanorama={active.handleOpenPanoramaFromDashboard}
          onViewPhotoInTour={active.handleOpenPanoramaFromTourManager}
          onUploadSuccess={panoData.handleUploadSuccess}
          mapStyle={mapStyle}
          defaultTab={dashboardDefaultTab}
          defaultCollectionId={dashboardDefaultCollectionId}
        />
      )}

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      {/* Immersive 360 viewer — blooms from map marker or portal */}
      <AnimatePresence>
        {active.activePanorama && (
          <motion.div
            key={active.activePanorama.id}
            initial={isEarlyOpen
              ? { scale: 1, opacity: 0 }
              : active.activePanorama.arrivedViaPortal
                ? { scale: 0.94, opacity: 0 }
                : { scale: 0,    opacity: 0 }
            }
            animate={{ scale: active.isTourTransitioning ? 1.09 : 1, opacity: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{
              scale: {
                duration: isEarlyOpen ? 0
                  : active.isTourTransitioning ? 0.42
                  : active.activePanorama.arrivedViaPortal ? 0.45 : 0.55,
                ease: active.isTourTransitioning ? [0.4, 0, 1, 1] : [0.16, 1, 0.3, 1],
              },
              opacity: isEarlyOpen
                ? { duration: 0.7, ease: "easeOut" }
                : { duration: 0.45, ease: "easeOut" },
            }}
            style={{
              transformOrigin: isEarlyOpen
                ? "50% 50%"
                : active.activePanorama.arrivedViaPortal
                  ? "50% 50%"
                  : `${active.viewerOrigin.x}px ${active.viewerOrigin.y}px`,
              touchAction: "none",
            }}
            className="absolute inset-0 z-50"
          >
            <PanoramaViewer
              panoramaId={active.activePanorama.id}
              baseTileUrl={active.activePanorama.baseTileUrl}
              previewUrl={active.activePanorama.previewUrl}
              width={active.activePanorama.width}
              height={active.activePanorama.height}
              poseHeading={active.activePanorama.poseHeading}
              posePitch={active.activePanorama.posePitch}
              poseRoll={active.activePanorama.poseRoll}
              nadirPatchUrl={active.activePanorama.nadirPatchUrl}
              captureDate={active.activePanorama.captureDate}
              creatorName={active.activePanorama.creatorName}
              isVerifiedCreator={active.activePanorama.isVerifiedCreator}
              ownerId={active.activePanorama.ownerId}
              defaultYaw={active.activePanorama.defaultYaw}
              defaultPitch={active.activePanorama.defaultPitch}
              defaultZoom={active.activePanorama.defaultZoom}
              introPanEnabled={active.activePanorama.introPanEnabled ?? true}
              postcardEnabled={active.activePanorama.postcardEnabled ?? true}
              postcardWatermark={active.activePanorama.postcardWatermark ?? null}
              spatialAudioEnabled={active.activePanorama.spatialAudioEnabled ?? true}
              ownerLogoUrl={user?.logo_url ?? null}
              arrivedViaPortal={active.activePanorama.arrivedViaPortal ?? false}
              onBack={viewerOnBack}
              timePairPanoramaId={active.activePanorama.timePairPanoramaId}
              timePairBaseTileUrl={active.activePanorama.timePairBaseTileUrl}
              onClose={() => {
                active.setActivePanorama(null);
                active.setTourHistory([]);
                tourRoute.notifyViewingEnd();
              }}
              onUpdate={panoData.loadPanoramas}
              onTourNavigate={active.handleTourNavigate}
              coordinates={activePanoCoords}
              isTourViewing={tourRoute.phase !== "idle" && tourRoute.phase !== "complete"}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating slideshow stop pill — visible while viewer is open */}
      <AnimatePresence>
        {slideshow.slideshowInterval && active.activePanorama && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0  }}
            exit={{   opacity: 0, y: 12  }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 bg-canvas/60 backdrop-blur-xl border border-emerald-500/30 rounded-full px-4 py-2 shadow-2xl"
          >
            <Shuffle className="w-3.5 h-3.5 text-emerald-400 animate-pulse shrink-0" />
            <span className="text-[11px] font-bold text-emerald-300 uppercase tracking-widest">
              Slideshow · {SLIDESHOW_INTERVALS.find((i) => i.ms === slideshow.slideshowInterval)?.shortLabel}
            </span>
            <button
              onClick={() => slideshow.setSlideshowInterval(null)}
              className="flex items-center gap-1 px-2.5 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded-full text-[10px] font-black uppercase tracking-wider transition-all border border-red-500/20"
            >
              <Square className="w-2.5 h-2.5" />
              Stop
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tour fade-to-black transition overlay */}
      <AnimatePresence>
        {active.isTourTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.38 }}
            className="fixed inset-0 z-[250] bg-black pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Exit-confirmation sheet — shown on any device when back-button history is exhausted */}
      <AnimatePresence>
        {exitDialogOpen && (
          <motion.div
            key="exit-dialog-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-end"
            onClick={cancelExit}
          >
            <motion.div
              key="exit-dialog-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 340, damping: 32 }}
              className="w-full bg-tone-950 border-t border-ink/10 rounded-t-3xl px-6 pt-5 pb-10"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-10 h-1 bg-ink/20 rounded-full mx-auto mb-6" />
              <h2 className="text-lg font-bold text-ink mb-1">Leave 360 Maps?</h2>
              <p className="text-sm text-tone-400 mb-6">You'll return to your browser.</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmExit}
                  className="w-full py-3.5 bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                >
                  Exit
                </button>
                <button
                  onClick={cancelExit}
                  className="w-full py-3.5 bg-ink/8 hover:bg-ink/12 text-ink/70 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </main>
  );
}
